import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiCall } from '../api/client';
import AudioButton from '../components/AudioButton';
import { MASTERED_INTERVAL } from '../constants';
import { LESSON_TYPES, lessonLabel } from '../utils/lessons';

const levelType = (l) => l.type || 'drill';
const isLessonLevel = (l) => LESSON_TYPES.includes(levelType(l));

// Levels in pedagogical order: by `order`, with array position as the tiebreaker.
function orderLevels(levels) {
  return (levels || [])
    .map((level, index) => ({ level, index }))
    .sort((a, b) => (a.level.order ?? 0) - (b.level.order ?? 0) || a.index - b.index)
    .map(x => x.level);
}

function formatDue(nextDue) {
  const now = new Date();
  const due = new Date(nextDue);
  const diffMs = due - now;
  if (diffMs <= 0) return 'Due now';
  const diffHours = diffMs / 3600000;
  // Sub-day intervals (e.g. the 6h/12h early steps) shouldn't round down to "Due now".
  if (diffHours < 1) return `Due in ${Math.max(1, Math.round(diffMs / 60000))}m`;
  if (diffHours < 24) return `Due in ${Math.min(23, Math.round(diffHours))}h`;
  const diffDays = Math.round(diffHours / 24);
  if (diffDays === 1) return 'Due tomorrow';
  if (diffDays < 7) return `Due in ${diffDays}d`;
  if (diffDays < 31) return `Due in ${Math.round(diffDays / 7)}w`;
  return `Due ${due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

function wordStatus(progress, now) {
  if (progress?.ignored) return { label: 'Ignored', cls: 'text-gray-400' };
  if (!progress || !progress.lastReviewed) return { label: 'Not started', cls: 'text-amber-300' };
  if ((progress.interval || 0) >= MASTERED_INTERVAL) return { label: 'Mastered ✓', cls: 'text-green-600 font-medium' };
  const isDue = new Date(progress.nextDue) <= now;
  return { label: formatDue(progress.nextDue), cls: isDue ? 'text-amber-600 font-medium' : 'text-sky-500' };
}

function LevelCard({ level, courseId, columnTypes, progressMap, onToggleIgnoreLevel, onToggleIgnoreWord, onStudy, isGuest }) {
  const [expanded, setExpanded] = useState(false);
  const sourceIsAudio = (columnTypes?.[level.sourceColumn] || 'text') === 'audio';
  const targetIsAudio = (columnTypes?.[level.targetColumn] || 'text') === 'audio';
  const total = level.entries.length;
  const ignored = level.entries.filter(e => progressMap[e.id]?.ignored).length;
  const allIgnored = total > 0 && ignored === total;
  const active = total - ignored;
  const seen = level.entries.filter(e => progressMap[e.id]?.lastReviewed && !progressMap[e.id]?.ignored).length;
  const mastered = level.entries.filter(e => (progressMap[e.id]?.interval || 0) >= MASTERED_INTERVAL && !progressMap[e.id]?.ignored).length;
  const now = new Date();
  const due = level.entries.filter(e => {
    const p = progressMap[e.id];
    return p && !p.ignored && p.lastReviewed && new Date(p.nextDue) <= now && p.interval < MASTERED_INTERVAL;
  }).length;
  const unseen = level.entries.filter(e => {
    const p = progressMap[e.id];
    return !p || (!p.ignored && !p.lastReviewed);
  }).length;
  const learnDone = unseen === 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-amber-100 p-5">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-semibold text-amber-800 text-lg">{level.title}</h3>
          <p className="text-amber-500 text-sm mt-0.5">{active} words · {mastered} mastered · {ignored} ignored</p>
        </div>
        {!isGuest && (
          <button
            onClick={() => onToggleIgnoreLevel(level.id, allIgnored)}
            className="text-xs text-amber-400 hover:text-amber-600 underline"
          >
            {allIgnored ? 'Unignore level' : 'Ignore level'}
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div className="w-full bg-amber-50 rounded-full h-2 mb-4">
        <div
          className="bg-amber-500 h-2 rounded-full transition-all"
          style={{ width: active > 0 ? `${(seen / active) * 100}%` : '0%' }}
        />
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => onStudy(level.id, 'learn')}
          disabled={learnDone}
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${learnDone
              ? 'bg-amber-50 text-amber-300 cursor-not-allowed'
              : 'bg-amber-100 hover:bg-amber-200 text-amber-800'
            }`}
        >
          {learnDone ? 'Learn ✓' : `Learn (${unseen})`}
        </button>
        <button
          onClick={() => onStudy(level.id, 'review')}
          disabled={due === 0}
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${due > 0
              ? 'bg-amber-500 hover:bg-amber-600 text-white'
              : 'bg-amber-50 text-amber-300 cursor-not-allowed'
            }`}
        >
          {due > 0 ? `Review (${due})` : 'Review'}
        </button>
      </div>

      {/* Expand toggle */}
      {total > 0 && (
        <button
          onClick={() => setExpanded(e => !e)}
          className="mt-3 w-full text-xs text-amber-400 hover:text-amber-600 flex items-center justify-center gap-1 pt-3 border-t border-amber-50 transition"
        >
          {expanded ? '▴ Hide words' : `▾ Show ${total} word${total === 1 ? '' : 's'}`}
        </button>
      )}

      {/* Word list */}
      {expanded && (
        <div className="mt-3 rounded-lg border border-amber-100 overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {level.entries.map((entry, i) => {
                const status = wordStatus(progressMap[entry.id], now);
                const wordIgnored = !!progressMap[entry.id]?.ignored;
                return (
                  <tr key={entry.id} className={i % 2 === 0 ? '' : 'bg-amber-50/40'}>
                    <td className="px-3 py-2 text-amber-900">
                      {sourceIsAudio ? <AudioButton src={entry.source} size="sm" /> : entry.source}
                    </td>
                    <td className="px-3 py-2 text-amber-600">
                      {targetIsAudio ? <AudioButton src={entry.target} size="sm" /> : entry.target}
                    </td>
                    <td className={`px-3 py-2 text-right text-xs whitespace-nowrap ${status.cls}`}>
                      {status.label}
                    </td>
                    {!isGuest && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          onClick={() => onToggleIgnoreWord(level.id, entry.id, wordIgnored)}
                          className="text-xs text-amber-400 hover:text-amber-600 underline"
                        >
                          {wordIgnored ? 'Unignore' : 'Ignore'}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// A hexagonal status badge for a curriculum cell — the hive metaphor: cells fill in
// as the learner completes them (done → amber comb with a check; current → ring;
// locked → muted with a lock).
function CellStatus({ status }) {
  const styles = {
    done: 'bg-amber-500 text-white border-amber-500',
    current: 'bg-white text-amber-600 border-amber-400 ring-2 ring-amber-200',
    available: 'bg-amber-50 text-amber-400 border-amber-200',
    locked: 'bg-gray-50 text-gray-300 border-gray-200',
  }[status];
  const glyph = status === 'done' ? '✓' : status === 'locked' ? '🔒' : '🐝';
  return (
    <div
      className={`flex-shrink-0 w-9 h-9 flex items-center justify-center border text-sm font-bold ${styles}`}
      style={{ clipPath: 'polygon(25% 5%, 75% 5%, 100% 50%, 75% 95%, 25% 95%, 0% 50%)' }}
      aria-hidden="true"
    >
      {glyph}
    </div>
  );
}

// A grammar/reading/writing lesson rendered as a curriculum cell.
function LessonCell({ level, locked, done, current, onOpen }) {
  return (
    <div className={`bg-white rounded-xl border p-5 flex items-start gap-4 ${locked ? 'border-gray-100 opacity-60' : 'border-amber-100'}`}>
      <CellStatus status={locked ? 'locked' : done ? 'done' : current ? 'current' : 'available'} />
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-wide text-amber-400">{lessonLabel(levelType(level))}{levelType(level) !== 'grammar-drill' && ' lesson'}</p>
        <h3 className="font-semibold text-amber-800 text-lg">{level.title}</h3>
        {levelType(level) === 'grammar-drill' ? (
          <p className="text-amber-500 text-sm mt-1 line-clamp-2">{level.grammarPoint} · {level.drillCount || 10} translations per round</p>
        ) : level.objectives?.length > 0 && (
          <p className="text-amber-500 text-sm mt-1 line-clamp-2">{level.objectives.join(' · ')}</p>
        )}
      </div>
      <button
        onClick={() => onOpen(level.id)}
        disabled={locked}
        className={`flex-shrink-0 self-center px-4 py-2 rounded-lg text-sm font-medium transition ${
          locked
            ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
            : done ? 'bg-amber-100 hover:bg-amber-200 text-amber-800' : 'bg-amber-500 hover:bg-amber-600 text-white'
        }`}
      >
        {locked ? 'Locked' : done ? 'Review' : 'Start'}
      </button>
    </div>
  );
}

// A drill cell shown locked (its words can't be studied until the prior level is done).
function LockedDrillCell({ level }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 flex items-center gap-4 opacity-60">
      <CellStatus status="locked" />
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-wide text-amber-300">drill</p>
        <h3 className="font-semibold text-amber-700 text-lg">{level.title}</h3>
        <p className="text-amber-400 text-sm mt-0.5">{(level.entries || []).length} words · complete the lesson above to unlock</p>
      </div>
    </div>
  );
}

// The ordered curriculum: lessons and drills interleaved, with sequential unlock.
// Drills reuse the existing LevelCard so their Learn/Review behaviour is unchanged.
function CourseCurriculum({ levels, gated, completedSet, drillProps, onOpenLesson }) {
  const ordered = orderLevels(levels);

  const isDone = (level) => {
    if (isLessonLevel(level)) return completedSet.has(level.id);
    const entries = level.entries || [];
    if (entries.length === 0) return true;
    return entries.every(e => {
      const p = drillProps.progressMap[e.id];
      return p && (p.lastReviewed || p.ignored);
    });
  };

  // Index of the first not-yet-done level — the learner's current spot.
  const currentIndex = ordered.findIndex(l => !isDone(l));

  return (
    <div className="space-y-4">
      {ordered.map((level, i) => {
        // Guests browse freely (no progress); authed learners progress sequentially.
        const locked = gated && i > 0 && !isDone(ordered[i - 1]);
        if (isLessonLevel(level)) {
          return (
            <LessonCell
              key={level.id}
              level={level}
              locked={locked}
              done={gated && isDone(level)}
              current={i === currentIndex}
              onOpen={onOpenLesson}
            />
          );
        }
        if (locked) return <LockedDrillCell key={level.id} level={level} />;
        return (
          <LevelCard
            key={level.id}
            level={level}
            courseId={drillProps.courseId}
            columnTypes={drillProps.columnTypes}
            progressMap={drillProps.progressMap}
            onToggleIgnoreLevel={drillProps.onToggleIgnoreLevel}
            onToggleIgnoreWord={drillProps.onToggleIgnoreWord}
            onStudy={drillProps.onStudy}
            isGuest={drillProps.isGuest}
          />
        );
      })}
    </div>
  );
}

export default function Course({ isAdmin }) {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { token, userId, isGuest } = useAuth();
  const [course, setCourse] = useState(null);
  const [columnTypes, setColumnTypes] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [progress, setProgress] = useState(null);   // lesson (courseProgress) record
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Re-render once a minute so the "Due in …" labels tick down live without a reload.
  const [, setNowTick] = useState(0);

  const onBack = () => navigate('/');

  async function load() {
    setLoading(true);
    setLoadError(null);
    try {
      const courseData = await apiCall('GET', `/courses/${courseId}`, null, token);
      setCourse(courseData);

      // Resolve column types (for audio columns): from the database for db-backed
      // courses, otherwise from the course itself.
      if (courseData.databaseId) {
        const db = await apiCall('GET', `/databases/${courseData.databaseId}`, null, token);
        setColumnTypes(db.columnTypes || []);
      } else {
        setColumnTypes(courseData.columnTypes || []);
      }

      // One course-wide request covers every level's cards and this user's
      // progress (lesson-type levels carry no entries, so they contribute nothing).
      const allProgress = {};
      const { cards } = await apiCall('GET', `/study/all/${courseId}`, null, token);
      cards.forEach(c => {
        if (c.progress) allProgress[c.entry.id] = c.progress;
      });
      setProgressMap(allProgress);

      // Lesson progress (completed lessons) — only for authed learners; guests browse
      // the curriculum without progression. Best-effort: failure leaves progress null.
      if (!isGuest && token && courseData.levels.some(isLessonLevel)) {
        try {
          setProgress(await apiCall('GET', `/courses/${courseId}/progress`, null, token));
        } catch (err) { console.error(err); }
      }
    } catch (err) {
      console.error(err);
      setLoadError(err.message || 'Failed to load course.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [courseId, token]);

  useEffect(() => {
    const id = setInterval(() => setNowTick(t => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  // Optimistically flip the `ignored` flag for the given entries so the UI updates
  // instantly; the server call follows and we reload to resync if it fails.
  function applyIgnore(entryIds, ignored) {
    setProgressMap(prev => {
      const next = { ...prev };
      for (const entryId of entryIds) {
        if (next[entryId]) {
          next[entryId] = { ...next[entryId], ignored };
        } else if (ignored) {
          next[entryId] = { ignored: true };
        }
      }
      return next;
    });
  }

  async function handleToggleIgnoreWord(levelId, entryId, currentlyIgnored) {
    const action = currentlyIgnored ? 'unignore' : 'ignore';
    applyIgnore([entryId], !currentlyIgnored);
    try {
      await apiCall('PATCH', `/study/${action}/${courseId}/${levelId}/${entryId}`, null, token);
    } catch (err) {
      console.error(err);
      load();
    }
  }

  async function handleToggleIgnoreLevel(levelId, allIgnored) {
    const level = course?.levels.find(l => l.id === levelId);
    if (!level) return;
    const action = allIgnored ? 'unignore-level' : 'ignore-level';
    applyIgnore(level.entries.map(e => e.id), !allIgnored);
    try {
      await apiCall('PATCH', `/study/${action}/${courseId}/${levelId}`, null, token);
    } catch (err) {
      console.error(err);
      load();
    }
  }

  function handleStudy(levelId, mode) {
    navigate(`/courses/${courseId}/study/${mode}${levelId ? `?level=${levelId}` : ''}`);
  }

  // Owner-only delete from the load-error screen, so a course whose word database
  // was deleted (and thus can't render) isn't stuck forever.
  async function handleDeleteCourse() {
    if (!window.confirm('Delete this course? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await apiCall('DELETE', `/courses/${courseId}`, null, token);
      navigate('/', { replace: true });
    } catch (err) {
      console.error(err);
      window.alert(err.message || 'Failed to delete course.');
      setDeleting(false);
    }
  }

  // Only drill levels participate in the learn/review stats and study UI below.
  // Lesson-type levels carry no entries and render in the lesson view (Phase 1).
  const drillLevels = (course?.levels || []).filter(l => (l.type || 'drill') === 'drill');

  // Calculate course-level stats
  const totalDue = drillLevels.reduce((acc, level) => {
    const now = new Date();
    return acc + level.entries.filter(e => {
      const p = progressMap[e.id];
      return p && !p.ignored && p.lastReviewed && new Date(p.nextDue) <= now && p.interval < MASTERED_INTERVAL;
    }).length;
  }, 0) || 0;

  const totalUnseen = drillLevels.reduce((acc, level) => {
    return acc + level.entries.filter(e => {
      const p = progressMap[e.id];
      return !p || (!p.ignored && !p.lastReviewed);
    }).length;
  }, 0) || 0;

  const allLearned = totalUnseen === 0;

  // Words with saved progress (seen, not ignored). The course-level Review reviews
  // all of these as a fallback when nothing is due, so it's only useless — and thus
  // disabled — when nothing has been learned yet.
  const totalLearned = drillLevels.reduce((acc, level) => {
    return acc + level.entries.filter(e => {
      const p = progressMap[e.id];
      return p && p.lastReviewed && !p.ignored;
    }).length;
  }, 0) || 0;

  const firstUnseenLevel = drillLevels.find(level =>
    level.entries.some(e => {
      const p = progressMap[e.id];
      return !p || (!p.ignored && !p.lastReviewed);
    })
  );

  // Whether this course carries authored lesson levels (the LLM-tutor curriculum). When
  // it does, the course detail shows the ordered lesson/drill map with sequential
  // unlock; otherwise it stays the plain drill-study view it has always been.
  const hasLessons = (course?.levels || []).some(isLessonLevel);
  const completedSet = new Set(progress?.completedLevels || []);

  if (loading) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center">
        <p className="text-amber-600">Loading course...</p>
      </div>
    );
  }

  // Deep links can point at a course that doesn't exist (or isn't visible);
  // show that instead of an empty shell. The course itself may have loaded even
  // though a later step failed (e.g. its word database was deleted) — in that
  // case its owner gets an escape hatch to edit or delete the course rather
  // than a dead end.
  if (loadError || !course) {
    const isOwner = !!course && !!userId && course.ownerId === userId;
    return (
      <div className="min-h-screen bg-amber-50 flex flex-col items-center justify-center gap-4 px-4">
        <p className="text-amber-700">{loadError || 'Course not found.'}</p>
        {isOwner && (
          <div className="flex gap-3">
            <button
              onClick={() => navigate(`/courses/${courseId}/edit`)}
              className="bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium px-5 py-2 rounded-lg transition"
            >
              Edit Course
            </button>
            <button
              onClick={handleDeleteCourse}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white text-sm font-medium px-5 py-2 rounded-lg transition"
            >
              {deleting ? 'Deleting…' : 'Delete Course'}
            </button>
          </div>
        )}
        <button onClick={onBack} className="text-amber-600 underline">← Back to dashboard</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50">
      {/* Header */}
      <header className="bg-white border-b border-amber-100 px-4 sm:px-6 py-4 flex items-center gap-3 sm:gap-4 sticky top-0 z-10">
        <button onClick={onBack} className="text-amber-500 hover:text-amber-700 text-lg flex-shrink-0">←</button>
        <h1 className="text-lg sm:text-xl font-bold text-amber-700 flex-1 min-w-0 truncate">{course?.title}</h1>
        {course?.ownerId === userId && (
          <button
            onClick={() => navigate(`/courses/${courseId}/edit`)}
            className="flex-shrink-0 text-sm font-medium text-amber-600 hover:text-amber-800 border border-amber-200 hover:border-amber-400 px-3 py-1.5 rounded-lg transition whitespace-nowrap"
          >
            Edit<span className="hidden sm:inline"> Course</span>
          </button>
        )}
      </header>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Course overview: image placeholder + details */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mb-8">
          {/* Course icon (falls back to a placeholder until one is uploaded) */}
          <div className="w-full h-32 sm:w-40 sm:h-40 flex-shrink-0 rounded-xl bg-amber-100 border border-amber-200 overflow-hidden flex items-center justify-center text-amber-400 text-sm">
            {course?.iconUrl ? (
              <img src={course.iconUrl} alt={`${course.title} icon`} className="w-full h-full object-cover" />
            ) : (
              'Image'
            )}
          </div>

          {/* Title, author/owner, description */}
          <div className="flex-1 min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-amber-800">{course?.title}</h2>
            <p className="text-amber-500 text-sm mt-1">
              {!course?.ownerId ? '(Account Deleted)' : course.ownerId === userId ? 'You' : (course.ownerName || 'Unknown user')}
            </p>
            {course?.description && (
              <p className="text-amber-600 mt-3">{course.description}</p>
            )}
          </div>
        </div>

        {/* Course-level action buttons. Hidden for lesson courses, where progression
            is driven by the curriculum map below (so "Learn" can't skip the grammar
            lesson that should precede a drill). A compact "Review due" stays available. */}
        {!hasLessons ? (
          <div className="flex gap-3 mb-8">
            <button
              disabled={allLearned}
              onClick={() => firstUnseenLevel && handleStudy(firstUnseenLevel.id, 'learn')}
              className={`flex-1 py-3 rounded-xl font-semibold transition ${allLearned
                  ? 'bg-amber-50 text-amber-300 cursor-not-allowed'
                  : 'bg-amber-500 hover:bg-amber-600 text-white'
                }`}
            >
              {allLearned ? 'Learn ✓' : `Learn (${totalUnseen})`}
            </button>
            <button
              disabled={totalLearned === 0}
              onClick={() => handleStudy(null, 'review')}
              className={`flex-1 py-3 rounded-xl font-semibold transition ${totalLearned === 0
                  ? 'bg-amber-50 text-amber-300 cursor-not-allowed'
                  : totalDue > 0
                    ? 'bg-amber-600 hover:bg-amber-700 text-white'
                    : 'bg-amber-100 hover:bg-amber-200 text-amber-700'
                }`}
            >
              {totalDue > 0 ? `Review (${totalDue})` : 'Review'}
            </button>
          </div>
        ) : totalDue > 0 && (
          <div className="mb-8">
            <button
              onClick={() => handleStudy(null, 'review')}
              className="w-full py-3 rounded-xl font-semibold bg-amber-600 hover:bg-amber-700 text-white transition"
            >
              Review due words ({totalDue})
            </button>
          </div>
        )}

        {/* Levels — the ordered lesson/drill curriculum for lesson courses, or the
            plain drill list for everything else. */}
        {hasLessons ? (
          <CourseCurriculum
            levels={course.levels}
            gated={!isGuest}
            completedSet={completedSet}
            onOpenLesson={(lessonId) => navigate(`/courses/${courseId}/lessons/${lessonId}`)}
            drillProps={{
              courseId,
              columnTypes,
              progressMap,
              onToggleIgnoreLevel: handleToggleIgnoreLevel,
              onToggleIgnoreWord: handleToggleIgnoreWord,
              onStudy: handleStudy,
              isGuest,
            }}
          />
        ) : (
          <div className="space-y-4">
            {drillLevels.map(level => (
              <LevelCard
                key={level.id}
                level={level}
                courseId={courseId}
                columnTypes={columnTypes}
                progressMap={progressMap}
                onToggleIgnoreLevel={handleToggleIgnoreLevel}
                onToggleIgnoreWord={handleToggleIgnoreWord}
                onStudy={handleStudy}
                isGuest={isGuest}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
